import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  List,
  Fab,
  Grid,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import { Refresh, FilterList, Add, Check } from "@mui/icons-material";
import "../OrganizerTab.css";
import DiaryCard from "./DiaryCard";
import DiaryModal from "./DiaryModal";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { useAuth } from "../../../contexts/AuthContext";
import { latinToCyrillic } from "../../SharedComponents/latinToCyrillic";

const API = process.env.REACT_APP_API_BASE;

const DiaryTab = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const { weddingId } = useAuth();

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API}/get-todos`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setTodos(data);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Грешка при вчитување на задачи");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodos();
  }, []);

  const handleDeleteTodo = (todoId) => {
    setTodos((prevTodos) => prevTodos.filter((todo) => todo.id !== todoId));
  };

  const handleToggleTodo = (todoId, nextChecked) => {
    setTodos((prevTodos) =>
      prevTodos.map((todo) =>
        todo.id === todoId ? { ...todo, checked: nextChecked } : todo
      )
    );
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleOpenFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleSelectFilter = (value) => {
    setStatusFilter(value);
    setFilterAnchorEl(null);
  };

  const filteredTodos = useMemo(() => {
    let list = todos;

    if (statusFilter === "completed") {
      list = list.filter((todo) => Boolean(todo.checked));
    } else if (statusFilter === "pending") {
      list = list.filter((todo) => !todo.checked);
    }

    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return list;
    }

    const tokenVariants = trimmedQuery
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean)
      .map((token) => {
        const variants = new Set([token]);
        const cyrillicVariant = latinToCyrillic(token);
        if (cyrillicVariant) {
          variants.add(cyrillicVariant);
        }
        return Array.from(variants);
      });

    if (!tokenVariants.length) {
      return list;
    }

    return list.filter((todo) => {
      const haystack = `${todo.title ?? ""} ${todo.description ?? ""}`.toLowerCase();
      const cyrillicHaystack = latinToCyrillic(haystack);
      return tokenVariants.every((variants) =>
        variants.some((variant) =>
          haystack.includes(variant) || cyrillicHaystack.includes(variant)
        )
      );
    });
  }, [todos, statusFilter, searchQuery]);

  const isFilterMenuOpen = Boolean(filterAnchorEl);

  return (
    <Grid
      item
      className="organizer-tab-container"
      size={{ xs: 10, md: 8, lg: 6, xl: 4 }}
    >
      <Box className="search-bar">
        <TextField
          fullWidth
          placeholder="Пребарувај"
          variant="outlined"
          size="small"
          className="search-input"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <IconButton color="inherit" onClick={fetchTodos}>
          <Refresh />
        </IconButton>
        <IconButton
          color={statusFilter === "all" ? "inherit" : "primary"}
          onClick={handleOpenFilter}
        >
          <FilterList />
        </IconButton>
      </Box>
      {filteredTodos.length >= 3 ? (
        <Button className="quick-add-strip" endIcon={<Add />} onClick={() => setModalOpen(true)}>
          Додадете задачи
        </Button>
      ) : null}
      <Menu
        anchorEl={filterAnchorEl}
        open={isFilterMenuOpen}
        onClose={handleCloseFilter}
      >
        {[
          { value: "all", label: "Сите задачи" },
          { value: "pending", label: "Активни" },
          { value: "completed", label: "Завршени" },
        ].map((option) => (
          <MenuItem
            key={option.value}
            selected={statusFilter === option.value}
            onClick={() => handleSelectFilter(option.value)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {statusFilter === option.value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {loading ? (
        <Box
          className="organizer-list"
          display="flex"
          justifyContent="center"
          p={2}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center" mt={2}>
          {error}
        </Typography>
      ) : filteredTodos.length > 0 ? (
        <>
          <Box className="organizer-list">
            <List>
              {filteredTodos.map((todo) => (
                <DiaryCard
                  key={todo.id}
                  id={todo.id}
                  title={todo.title}
                  description={todo.description}
                  checked={todo.checked}
                  onSuccess={() => handleDeleteTodo(todo.id)}
                  onToggle={handleToggleTodo}
                />
              ))}
            </List>
          </Box>
          <Box
            className="fab-container"
            display="flex"
            justifyContent="flex-end"
          >
            <Fab className="add-fab" onClick={() => setModalOpen(true)}>
              <Add />
            </Fab>
          </Box>
        </>
      ) : (
        <Grid
          container
          spacing={2}
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item>
            <Typography variant="h6" textAlign={{ xs: "center", md: "left" }}>
              {searchQuery || statusFilter !== "all"
                ? "Нема задачи што ги исполнуваат условите."
                : "На копчето додадете задачи"}
            </Typography>
          </Grid>
          <Grid item>
            <Fab className="add-fab" onClick={() => setModalOpen(true)}>
              <Add />
            </Fab>
          </Grid>
        </Grid>
      )}

      <DiaryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          fetchTodos();
          setModalOpen(false);
        }}
        weddingId={weddingId}
      />
    </Grid>
  );
};

export default DiaryTab;
